import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { itineraryRequest, setCurrentTripId } from '../../store/slices/cruise';
import Modal from '../Modal';
import CruiseDetails from './CruiseDetails';
import TabPanel from '../TabPanel';
import FlightInformation from './FlightInformation';
import CruiseInformation from './CruiseInformation';
import HotelInformation from './HotelInformation';
import CarsInformation from './CarsInformation';
import TripConfiguration from '../TripConfiguration';
import AgentTipInformation from './AgentTipInformation';
import {
  SearchStatus,
  doEnableAgentTips,
  TravelTypes,
  SearchModes,
  TravelOptionTab,
} from '../../constants';
import {
  updateFlightInAgentTipRequest,
  updateHotelInAgentTipRequest,
} from '../../store/slices/intelligence';
import getLowestCostItineraryFromItineraryRows from '../../helpers/getLowestCostItineraryFromItineraryRows';
import TravelOptionTabs from './TravelOptionTabs';
import HotelItinerary from './HotelInformation/HotelItinerary';
import FlightItinerary from './FlightInformation/FlightItinerary';
import CarItinerary from './CarsInformation/CarItinerary';
import convertTravelTypeToTravelOptionTab from '../../helpers/convertTravelTypeToTravelOptionTab';
import CustomInventoryItinerary from './CustomInventoryInformation/CustomInventoryItinerary';
import checkDoSkipTripConfiguration from '../../helpers/selectors/checkDoSkipTripConfiguration';
import ChatGPTInterface from '../ChatGPTInterface';
import useCruiseExtractor from '../ChatGPTInterface/extractors/cruiseExtractor';
import { isQueryRelevantToCruise } from '../ChatGPTInterface/helpers/isQueryRelevant';
import isQuerySensitive from '../ChatGPTInterface/helpers/isQuerySensitive';
import {
  useNLPConstants,
  useNLPRelevantTerms,
  TEMP_NUMBER,
  useNLPInstructions,
} from '../ChatGPTInterface/constants/nlpSettings';
import { generateCruiseGeneralIntroMessage } from '../ChatGPTInterface/introMessages/cruiseIntroMessage';
import TourItinerary from './TourInformation/TourItinerary';
import TourInformation from './TourInformation';
import TourBookingColumn from './TourInformation/TourBookingColumn';
import { setIsUserInteractedWithHotelList } from '../../store/slices/hotel';
import { toggleMapVisibility } from '../../store/slices/tripSearch';
import useStyles from './styles';
import CustomInventoryInformation from './CustomInventoryInformation';
import checkDoEnableTripConfigDecoupling from '../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import checkDoTripExistInBrochure from '../../helpers/selectors/checkDoTripExistInBrochure';

const TravelOption = ({
  tripId,
  isInsideModal,
  onModalClose,
  parentSelectedItineraryRow,
  doShowOnlyCruiseTab,
  onItineraryMouseEnter,
  onItineraryMouseLeave,
  isExpanded,
  travelOptionIndex,
  firstTravelOptionTab,
  travelOption,
  selectedTab,
  setSelectedTab,
}) => {
  const router = useRouter();
  const classes = useStyles();
  const { doEnableAgentTips: doEnableAgentTipsFromQueryString, debug } = router.query;
  const doViewDebugInfo = debug === 'true';
  const { isLoggedIn, travelAgency = {} } = useSelector((state) => state.user);
  const doShowBookButton = useSelector(checkDoSkipTripConfiguration);
  const selectedSearchMode = useSelector((state) => state.tripSearch.selectedSearchMode);
  const doEnableAgentTipsFeature =
    isLoggedIn &&
    (doEnableAgentTips || doEnableAgentTipsFromQueryString === 'true') &&
    selectedSearchMode === SearchModes.CRUISE_TRIP;
  const { track } = useAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentOpenItem, setCurrentOpenItem] = useState();
  const [doShowTripConfiguration, setDoShowTripConfiguration] = useState(false);
  // The availability panel takes the column the trip configuration will take, so a card shows one or
  // the other: picking a departure ends with the tour in the trip.
  const [isTourBookingOpen, setIsTourBookingOpen] = useState(false);
  const doShowTourBooking = isTourBookingOpen && travelOption.mode === TravelTypes.tour;
  const isMapShown = useSelector((state) => state.tripSearch.isMapShown);
  const doRestoreMap = useRef(false);
  const [isScrollingNeeded, setIsScrollingNeeded] = useState(false);

  // Get NLP related settings
  const nlpConstants = useNLPConstants();
  const MAX_CRUISE_STATEROOMS_ALLOWED_USING_NLP = nlpConstants
    ? parseInt(nlpConstants.MAX_CRUISE_STATEROOMS_ALLOWED_USING_NLP.value, 10)
    : TEMP_NUMBER;
  const nlpRelevantTerms = useNLPRelevantTerms();
  const nlpInstructions = useNLPInstructions();

  const trip = useSelector((state) => state.trip.selectedTrips[tripId]);
  const [selectedItineraryRow, setSelectedItineraryRow] = useState(
    parentSelectedItineraryRow || getLowestCostItineraryFromItineraryRows(travelOption),
  );
  const dispatch = useDispatch();

  const searchObj = useSelector((state) => state.tripSearch.searchObj.cruise);
  const { value: flightOriginGeoIndex } = searchObj.filters.origin || {};
  const agentTips = useSelector(
    (state) =>
      state.intelligence.agentTips[`${selectedItineraryRow.itineraryId}-${flightOriginGeoIndex}`],
  );

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);

  // The panel is wide enough that the map has to give way, and it comes back the way it was left.
  useEffect(() => {
    if (doShowTourBooking && isMapShown) {
      doRestoreMap.current = true;
      dispatch(toggleMapVisibility(false));
    }
    // A trip in the column keeps the map away on its own, so the map waits for the trip to be reset
    // rather than coming back the moment the panel closes behind an added tour.
    if (!doShowTourBooking && !doShowTripConfiguration && doRestoreMap.current) {
      doRestoreMap.current = false;
      dispatch(toggleMapVisibility(true));
    }
  }, [doShowTourBooking, doShowTripConfiguration, isMapShown, dispatch]);

  // Adding the tour closes the panel the way pressing "Hide availability" would, so the map comes
  // back on the same path rather than through a second rule.
  useEffect(() => {
    if (doShowTripConfiguration && isTourBookingOpen) {
      setIsTourBookingOpen(false);
    }
  }, [doShowTripConfiguration, isTourBookingOpen]);

  const handleTabChange = (index) => {
    setSelectedTab(travelOptionIndex, index);
    dispatch(setIsUserInteractedWithHotelList(true));
    track('travelOption.handleTabChange', {
      index,
    });
  };

  const handleItineraryRowClick = (row) => {
    setSelectedItineraryRow(row);
    track('travelOption.handleItineraryRowClick', {
      row: JSON.stringify(row),
    });
  };

  const handleViewCabinsClick = (itineraryId) => {
    if (isInsideModal && onModalClose) {
      onModalClose();
      dispatch(setCurrentTripId(null));
    } else {
      if (!isModalOpen) {
        dispatch(
          itineraryRequest(
            typeof itineraryId === 'number' ? itineraryId : selectedItineraryRow.itineraryId,
          ),
        );
        dispatch(setCurrentTripId(tripId));
      }
      setIsModalOpen((v) => !v);
    }

    track('travelOption.handleViewCabinsClick', {
      itineraryId: selectedItineraryRow.itineraryId,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    track('travelOption.handleCloseModal');
  };

  const handleDoShowAgentTip = (currentIndex) => {
    setCurrentOpenItem(currentIndex);
    const index = 4;
    setIsScrollingNeeded(true);
    setSelectedTab(travelOptionIndex, index);
    track('travelOption.handleTabChange', {
      index,
    });

    const hotelItinerary =
      agentTips.tips[currentIndex] &&
      agentTips.tips[currentIndex].itineraries &&
      _.find(agentTips.tips[currentIndex].itineraries, (i) => i.mode === TravelTypes.hotel);
    if (
      hotelItinerary &&
      hotelItinerary.searchGeoname &&
      hotelItinerary.searchGeoname.departureCityId &&
      agentTips.tips[currentIndex] &&
      agentTips.tips[currentIndex].hotelStatus !== SearchStatus.SEARCHING &&
      agentTips.tips[currentIndex].hotelStatus !== SearchStatus.DONE
    ) {
      dispatch(
        updateHotelInAgentTipRequest({
          cruiseItineraryId: selectedItineraryRow.itineraryId,
          cruiseDepartureCityId: hotelItinerary.searchGeoname.departureCityId,
          agentTipId: currentIndex,
          checkin: hotelItinerary.startDateTime,
          checkout: hotelItinerary.endDateTime,
          hotelId: hotelItinerary.hotel.ids,
          flightOriginGeoIndex,
          searchGeoname: hotelItinerary.searchGeoname,
        }),
      );
    }

    const betterFlight =
      agentTips.tips[currentIndex] &&
      !_.find(agentTips.tips[currentIndex].itineraries, (i) => i.mode === TravelTypes.flight) &&
      agentTips.tips[currentIndex].betterFlight;
    if (
      betterFlight &&
      agentTips.tips[currentIndex].flightStatus !== SearchStatus.SEARCHING &&
      agentTips.tips[currentIndex].flightStatus !== SearchStatus.DONE
    ) {
      dispatch(
        updateFlightInAgentTipRequest({
          cruiseItineraryId: selectedItineraryRow.itineraryId,
          agentTipId: currentIndex,
          flightOriginGeoIndex,
          betterFlight,
        }),
      );
    }
  };

  useEffect(() => {
    setDoShowTripConfiguration(
      !isInsideModal &&
        trip &&
        !doShowOnlyCruiseTab &&
        !doShowBookButton &&
        !doEnableTripConfigDecoupling,
    );
  }, [trip, isInsideModal, doShowOnlyCruiseTab, doShowBookButton, doEnableTripConfigDecoupling]);

  useEffect(() => {
    if (isScrollingNeeded) {
      setIsScrollingNeeded(false);
    }
  }, [isScrollingNeeded]);

  useEffect(() => {
    if (travelOption) {
      setSelectedTab(travelOptionIndex, convertTravelTypeToTravelOptionTab(travelOption.mode));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  // On click of 'Add hotel', 'Add card', 'Add flight' in trip configuration
  // change the tab of first Travel Option.
  useEffect(() => {
    if (travelOptionIndex === 0 && firstTravelOptionTab) {
      setSelectedTab(travelOptionIndex, firstTravelOptionTab);
    }
  }, [travelOptionIndex, firstTravelOptionTab, setSelectedTab]);

  const staterooms = useSelector((state) => state.tripSearch.searchObj.staterooms);
  const doTripExistInBrochure = useSelector(checkDoTripExistInBrochure(travelOption, staterooms));
  return (
    <div
      key={tripId}
      id={tripId}
      className={clsx(
        classes.mobileGridLayout,
        'parentDiv border border-gray-300 bg-white shadow rounded-md w-full mx-auto mb-5 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4',
      )}
    >
      <div
        className={clsx(
          doShowTripConfiguration || doShowTourBooking
            ? 'md:col-span-2 xl:col-span-3'
            : 'md:col-span-3 xl:col-span-4',
          'flex flex-col min-w-0',
        )}
      >
        {!doShowOnlyCruiseTab ? (
          <>
            <TravelOptionTabs
              isScrollingNeeded={isScrollingNeeded}
              isInsideModal={isInsideModal}
              handleTabChange={handleTabChange}
              selectedTab={selectedTab}
              doEnableAgentTipsFeature={doEnableAgentTipsFeature}
              isCruiseInBrochure={doTripExistInBrochure}
              itineraryId={selectedItineraryRow.itineraryId}
              travelOptionMode={travelOption.mode}
            />
            <TabPanel value={selectedTab} index={TravelOptionTab.FLIGHT}>
              {travelOption.mode === TravelTypes.flight ? (
                <FlightItinerary
                  itinerary={travelOption.flightItinerary}
                  doHaveBorderAround={false}
                  doShowPrice
                  tripId={tripId}
                  itineraryIndex={travelOptionIndex}
                />
              ) : (
                <div className="mx-10 my-5">
                  <FlightInformation
                    cruiseItineraryId={selectedItineraryRow.itineraryId}
                    tripId={tripId}
                    travelOption={travelOption}
                  />
                </div>
              )}
            </TabPanel>
            <TabPanel value={selectedTab} index={TravelOptionTab.HOTEL}>
              {travelOption.mode === TravelTypes.hotel ? (
                <HotelItinerary
                  id={travelOption.hotelItinerary.id}
                  key={travelOption.hotelItinerary.id}
                  itinerary={travelOption.hotelItinerary}
                  tripId={tripId}
                  onModalClose={onModalClose}
                  isInsideModal={isInsideModal}
                  doHaveBorderAround={false}
                  onMouseEnter={onItineraryMouseEnter}
                  onMouseLeave={onItineraryMouseLeave}
                  isExpanded={isExpanded}
                  doShowChatGpt={
                    !travelAgency.settings || travelAgency.settings.doShowChatGPTOnResults
                  }
                  travelOption={travelOption}
                />
              ) : (
                <HotelInformation
                  cruiseItineraryId={selectedItineraryRow.itineraryId}
                  departureCityName={travelOption && travelOption.itineraryDepartCity}
                  tripId={tripId}
                  searchDestination={
                    (travelOption &&
                      travelOption.flightItinerary &&
                      travelOption.flightItinerary.flightSegments[0].destinationGeoname) ||
                    (travelOption &&
                      travelOption.carItinerary &&
                      travelOption.carItinerary.destinationGeoname)
                  }
                  doShowChatGpt={
                    !travelAgency.settings || travelAgency.settings.doShowChatGPTOnResults
                  }
                  travelOption={travelOption}
                />
              )}
            </TabPanel>
            <TabPanel value={selectedTab} index={TravelOptionTab.GROUND}>
              {travelOption.mode === TravelTypes.carrental ? (
                <CarItinerary
                  tripId={tripId}
                  itinerary={travelOption.carItinerary}
                  doHaveBorderAround={false}
                />
              ) : (
                <CarsInformation
                  tripId={tripId}
                  cruiseItineraryId={selectedItineraryRow && selectedItineraryRow.itineraryId}
                  travelOption={travelOption}
                />
              )}
            </TabPanel>
            <TabPanel value={selectedTab} index={TravelOptionTab.TOUR}>
              {travelOption.mode === TravelTypes.tour ? (
                <TourItinerary
                  itinerary={travelOption.itinerary}
                  isBookingOpen={isTourBookingOpen}
                  isBookingDisabled={doShowTripConfiguration}
                  onToggleBooking={() => setIsTourBookingOpen((v) => !v)}
                  doHaveBorderAround={false}
                  onMouseEnter={onItineraryMouseEnter}
                  onMouseLeave={onItineraryMouseLeave}
                  isExpanded={isExpanded}
                />
              ) : (
                <TourInformation tripId={tripId} />
              )}
            </TabPanel>
            {travelAgency && travelAgency.searchbarTab && (
              <TabPanel
                value={selectedTab}
                index={TravelOptionTab.CUSTOM}
                key={travelAgency.searchbarTab.id}
              >
                {travelOption.mode === TravelTypes.custom ? (
                  <CustomInventoryItinerary
                    tripId={tripId}
                    itinerary={travelOption.itinerary}
                    doHaveBorderAround={false}
                    onMouseEnter={onItineraryMouseEnter}
                    onMouseLeave={onItineraryMouseLeave}
                    isExpanded={isExpanded}
                  />
                ) : (
                  <CustomInventoryInformation
                    tripId={tripId}
                    doHaveBorderAround={false}
                    onMouseEnter={onItineraryMouseEnter}
                    onMouseLeave={onItineraryMouseLeave}
                    isExpanded={isExpanded}
                  />
                )}
              </TabPanel>
            )}
            {doEnableAgentTipsFeature && (
              <TabPanel value={selectedTab} index={TravelOptionTab.TIPS}>
                <AgentTipInformation
                  currentOpenItem={currentOpenItem}
                  itineraryId={selectedItineraryRow.itineraryId}
                  tripId={tripId}
                  handleViewCabinsClick={handleViewCabinsClick}
                  handleCloseCruiseModal={handleCloseModal}
                  doViewDebugInfo={doViewDebugInfo}
                  travelOption={travelOption}
                />
              </TabPanel>
            )}
          </>
        ) : (
          ''
        )}
        {travelOption.mode === TravelTypes.cruise && (
          <TabPanel value={selectedTab} index={TravelOptionTab.CRUISE}>
            <CruiseInformation
              isInsideModal={isInsideModal}
              onModalClose={onModalClose}
              parentSelectedItineraryRow={parentSelectedItineraryRow}
              selectedItineraryRow={selectedItineraryRow}
              handleViewCabinsClick={handleViewCabinsClick}
              isModalOpen={isModalOpen}
              handleCloseModal={handleCloseModal}
              handleItineraryRowClick={handleItineraryRowClick}
              handleDoShowAgentTip={handleDoShowAgentTip}
              canShowAgentTips={doEnableAgentTipsFeature}
              tripId={tripId}
            />
            {Object.keys(staterooms).length <= MAX_CRUISE_STATEROOMS_ALLOWED_USING_NLP &&
              (!travelAgency.settings || travelAgency.settings.doShowChatGPTOnResults) &&
              travelOption.itineraryRows.map((row) => (
                <div
                  className="sm:py-4 sm:px-8"
                  key={row.itineraryId}
                  style={{
                    display:
                      selectedItineraryRow.itineraryId === row.itineraryId ? 'block' : 'none',
                  }}
                >
                  <ChatGPTInterface
                    rawData={{ travelOption, selectedItineraryRow }}
                    extractor={useCruiseExtractor}
                    utilFunctions={{
                      isQueryRelevant: isQueryRelevantToCruise(nlpRelevantTerms),
                      isQuerySensitive,
                    }}
                    disabled={false}
                    generalIntro={generateCruiseGeneralIntroMessage(
                      travelOption,
                      selectedItineraryRow,
                      nlpConstants,
                      nlpInstructions,
                    )}
                    siteType="CRUISE"
                    instructionName="Interface - Cruise Instruction"
                  />
                </div>
              ))}
            <Modal open={isModalOpen} onClose={handleCloseModal}>
              <div className="border border-gray-300 shadow mb-5 rounded-lg">
                <TravelOptionTabs
                  isScrollingNeeded={isScrollingNeeded}
                  isInsideModal
                  handleTabChange={handleTabChange}
                  selectedTab={selectedTab}
                  doEnableAgentTipsFeature={doEnableAgentTipsFeature}
                  isCruiseInBrochure={doTripExistInBrochure}
                  itineraryId={selectedItineraryRow.itineraryId}
                  travelOptionMode={travelOption.mode}
                />
                <CruiseInformation
                  isInsideModal
                  onModalClose={onModalClose}
                  parentSelectedItineraryRow={parentSelectedItineraryRow}
                  selectedItineraryRow={selectedItineraryRow}
                  handleViewCabinsClick={handleViewCabinsClick}
                  isModalOpen={isModalOpen}
                  handleCloseModal={handleCloseModal}
                  handleItineraryRowClick={handleItineraryRowClick}
                  handleDoShowAgentTip={handleDoShowAgentTip}
                  canShowAgentTips={doEnableAgentTipsFeature}
                  tripId={tripId}
                />
              </div>
              <CruiseDetails
                tripId={tripId}
                onModalClose={handleCloseModal}
                itineraryId={selectedItineraryRow.itineraryId}
                travelOption={travelOption}
              />
            </Modal>
          </TabPanel>
        )}
      </div>
      {doShowTourBooking && (
        <TourBookingColumn itinerary={travelOption.itinerary} tripId={tripId} />
      )}
      {doShowTripConfiguration && (
        <TripConfiguration tripId={tripId} onTabChange={handleTabChange} />
      )}
    </div>
  );
};

TravelOption.propTypes = {
  isInsideModal: PropTypes.bool,
  onModalClose: PropTypes.func,
  // oxlint-disable-next-line react/forbid-prop-types
  parentSelectedItineraryRow: PropTypes.object,
  tripId: PropTypes.string,
  doShowOnlyCruiseTab: PropTypes.bool,
  onItineraryMouseEnter: PropTypes.func,
  onItineraryMouseLeave: PropTypes.func,
  isExpanded: PropTypes.shape({ value: PropTypes.bool }),
  travelOptionIndex: PropTypes.number,
  firstTravelOptionTab: PropTypes.number,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
    itineraryDepartCity: PropTypes.string,
    itinerary: PropTypes.shape(),
    itineraryRows: PropTypes.arrayOf(PropTypes.shape()),
    flightItinerary: PropTypes.shape({
      flightSegments: PropTypes.arrayOf(PropTypes.shape()),
      flightKey: PropTypes.string,
    }),
    hotelItinerary: PropTypes.shape({
      id: PropTypes.number,
    }),
    carItinerary: PropTypes.shape({
      id: PropTypes.number,
      destinationGeoname: PropTypes.shape(),
    }),
  }).isRequired,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func.isRequired,
};

TravelOption.defaultProps = {
  isInsideModal: false,
  onModalClose: undefined,
  parentSelectedItineraryRow: undefined,
  tripId: undefined,
  doShowOnlyCruiseTab: false,
  onItineraryMouseEnter: undefined,
  onItineraryMouseLeave: undefined,
  isExpanded: { value: false },
  travelOptionIndex: undefined,
  firstTravelOptionTab: undefined,
  selectedTab: undefined,
};

export default React.memo(TravelOption);
